import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { usePhoneNavigation } from '@/shell/usePhoneNavigation';
import { phoneNoticeBottom, useDockedBarsRise } from '@/features/decisions/useDockedBarsRise';
import { busyControlProps } from '@/utils/busyControl';
import { gateControlProps } from '@/utils/gateControl';
import type { WorkAreaGateType } from '@/utils/workAreaPermissionGate';
import { TOUCH_TARGET } from './touchTarget';
import { banners } from '@featit/shared/i18n/messages/en/banners';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT } from '@/i18n/LocaleProvider';

registerEnglishArea('banners', banners);

/** The toast's one next step: a button, or a link when it goes somewhere. */
export interface IConfirmationToastAction {
  label: string;
  icon?: LucideIcon;
  testId: string;
  onClick?: () => void;
  /** An app path: the action is then a link (`View` on *Added*); `onClick` still runs as it opens. */
  to?: string;
  disabled?: boolean;
  /**
   * I-2317: the action's work is running (an Undo on its way). The action keeps the focus it was
   * pressed with and takes no second press; `disabled` is for its own reasons only.
   */
  busy?: boolean;
  /**
   * F-0784 OR-40: the work-area gate the action's write answers to. Refused, the action stays shown,
   * `aria-disabled`, with the gate's reason as its title and description, and a press does nothing
   * (`gateControlProps`, disable-not-hide). Without it the action is not gated, as before.
   */
  gate?: WorkAreaGateType;
}

/** A failure of the action itself (a failed Undo), said above the pill with its Retry. */
export interface IConfirmationToastFailure {
  message: string;
  testId: string;
  retryTestId: string;
  onRetry: () => void;
}

/*
 * I-1190: one layout for every confirmation toast. A one-line toast is one height with or without
 * an action — 52 px where a finger is the pointer (the action's 44 px target and 4 px above and
 * below), 40 px otherwise — with the same padding and the action in the same place. A long
 * message wraps inside its own column (`min-w-0`) and the action keeps its place at the trailing
 * end, never dropping to a line of its own; the corners keep their one-line radius.
 */
const PILL =
  'pointer-events-auto flex min-h-10 max-w-[calc(100vw-32px)] items-center gap-3 rounded-[26px] bg-[var(--evx-elevated)] py-1 pl-4 text-[13px] text-[var(--evx-text)] shadow-[var(--evx-shadow-2)] ring-1 ring-[var(--evx-border)] max-sm:min-h-[52px] [@media(pointer:coarse)]:min-h-[52px]';
const ACTION = `inline-flex min-h-8 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 text-[13px] font-medium text-[var(--evx-accent)] ${TOUCH_TARGET} evx-link-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--evx-focus)] disabled:opacity-60 data-[busy]:cursor-wait data-[busy]:opacity-60`;

/**
 * A confirmation — *Archived I-0016 · Undo*, *Added I-0123 · View*, *Answered Drop it. ·
 * Take it back*, a row action's answer — drawn the one way (I-1190). On a phone it stands above the
 * tab bar, mobile web's own bar and its ＋, and any docked bar, on the one offset every phone notice
 * takes (I-1152); from `sm` it sits 24 px off the bottom, or off the top of a pane's bottom panel
 * (I-1831). The frame lets taps through beside the
 * pill. Mount it only while there is something to say: the caller owns when it shows.
 */
export function ConfirmationToast({
  testId,
  role = 'status',
  message,
  action,
  failure,
}: {
  testId: string;
  role?: 'status' | 'alert';
  message: ReactNode;
  action?: IConfirmationToastAction;
  failure?: IConfirmationToastFailure | null;
}) {
  const t = useT();
  const phone = usePhoneNavigation();
  const rise = useDockedBarsRise(phone);
  if (typeof document === 'undefined') return null;

  const Icon = action?.icon;
  const actionContent = action ? (
    <>
      {Icon ? <Icon className="h-[14px] w-[14px]" aria-hidden="true" /> : null}
      {action.label}
    </>
  ) : null;

  return createPortal(
    <div
      role={role}
      data-testid={testId}
      // F-0740 ARV-33: a floating layer, so it steps aside while the iOS shell takes a capture.
      data-featit-overlay=""
      className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4"
      // I-1831: off a phone, above a pane's bottom panel while one is drawn (`--evx-pane-foot`).
      // F-0781 (h): and above the shell's bottom region (`--bottom-bars-height`), never over its bars.
      // F-0718: and over the pane that lifts it, where one says its sides (`--evx-pane-left/right`).
      style={{
        bottom: phone
          ? phoneNoticeBottom(rise)
          : 'calc(1.5rem + var(--evx-pane-foot, 0px) + var(--bottom-bars-height, 0px))',
        left: phone ? undefined : 'var(--evx-pane-left, 0px)',
        right: phone ? undefined : 'var(--evx-pane-right, 0px)',
      }}
    >
      <div className={`${PILL} ${action ? 'pr-2' : 'pr-4'}`} data-toast-pill="">
        <span data-toast-message="" className="min-w-0 py-1.5 [overflow-wrap:anywhere]">
          {message}
        </span>
        {action?.to ? (
          <Link
            to={action.to}
            onClick={action.onClick}
            data-testid={action.testId}
            data-toast-action=""
            className={ACTION}
          >
            {actionContent}
          </Link>
        ) : action ? (
          <button
            type="button"
            data-testid={action.testId}
            data-toast-action=""
            {...(action.gate
              ? gateControlProps(action.gate, {
                  busy: action.busy ?? false,
                  otherwiseDisabled: action.disabled,
                })
              : busyControlProps(action.busy ?? false, { disabled: action.disabled }))}
            onClick={action.onClick}
            className={ACTION}
          >
            {actionContent}
          </button>
        ) : null}
      </div>
      {failure ? (
        <div
          role="alert"
          data-testid={failure.testId}
          className="pointer-events-auto absolute inset-x-0 -top-10 mx-auto w-max whitespace-nowrap rounded-md bg-red-50 px-3 py-1.5 text-[12px] text-red-700 shadow dark:bg-red-950/60 dark:text-red-200"
        >
          {failure.message}{' '}
          <button
            type="button"
            data-testid={failure.retryTestId}
            onClick={failure.onRetry}
            className="ml-1 font-medium evx-banner-action"
          >
            {t('banners.retry')}
          </button>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
